import Vue from 'vue';
// Local Imports
import App from './App.vue';
import router from './router';
import store from './store';
import i18n from './i18n';
import HoneybadgerVue from '@honeybadger-io/vue';
// Libraries
import axios from 'axios';
import Notifications from 'vue-notification';
import { BootstrapVue } from 'bootstrap-vue';
import VueAnalytics from 'vue-analytics';
import Moment from 'vue-moment';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import UUID from 'vue-uuid';
import Vuex from 'vuex';
// Font Awesome
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  faTachometer,
  faSearch,
  faCommentAltSlash,
  faEye,
  faEyeSlash,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faCommentAltMedical as falCommentAltMedical,
  faVirus,
  faUserCog,
  faChevronRight,
  faHourglassHalf,
  faTimesCircle,
  faUser,
  faBell,
  faFile,
  faSquare,
  faQuestionCircle,
  faCheck,
  faEnvelope,
  faUserMdChat,
  faHouseUser,
  faFileMedical,
  faCommentAltEdit,
  faLaptopHouse,
  faHandsWash,
  faPeopleArrows,
  faHeadSideCough,
  faBoxFull,
  faPumpMedical,
  faShip,
  faUserMd,
  faLocation,
  faBookReader as falBookReader,
  faVirus as falVirus,
  faTachometer as falTachometer,
  faLock,
  faUserFriends,
  faNotesMedical,
  faUserShield,
} from '@fortawesome/pro-light-svg-icons';
import {
  faBookReader as fasBookReader,
  faCircle,
} from '@fortawesome/free-solid-svg-icons';
import {
  faAlarmExclamation,
  faVirus as fasVirus,
  faCheckSquare,
  faExclamationTriangle,
  faCheckCircle,
  faArrowLeft,
  faCommentAltMedical as fasCommentAltMedical,
  faTachometer as fasTachometer,
} from '@fortawesome/pro-solid-svg-icons';
// import css styles
import './styles/main.scss';
// JWT Services
import { destroyToken, getToken } from './services/jwt.service';

// Font Awesome
library.add(faTachometer);
library.add(falTachometer);
library.add(fasTachometer);
library.add(fasCommentAltMedical);
library.add(falCommentAltMedical);
library.add(fasBookReader);
library.add(falBookReader);
library.add(faVirus);
library.add(fasVirus);
library.add(falVirus);
library.add(faCommentAltSlash);
library.add(faAlarmExclamation);
library.add(faUserCog);
library.add(faChevronRight);
library.add(faHourglassHalf);
library.add(faCheckSquare);
library.add(faSquare);
library.add(faTimesCircle);
library.add(faSearch);
library.add(faUser);
library.add(faBell);
library.add(faFile);
library.add(faQuestionCircle);
library.add(faExclamationTriangle);
library.add(faCheckCircle);
library.add(faCheck);
library.add(faEnvelope);
library.add(faArrowLeft);
library.add(faUserMdChat);
library.add(faHouseUser);
library.add(faFileMedical);
library.add(faCommentAltEdit);
library.add(faLaptopHouse);
library.add(faHandsWash);
library.add(faPeopleArrows);
library.add(faHeadSideCough);
library.add(faBoxFull);
library.add(faPumpMedical);
library.add(faShip);
library.add(faUserMd);
library.add(faLocation);
library.add(faCircle);
library.add(faEye);
library.add(faEyeSlash);
library.add(faLock);
library.add(faUserFriends);
library.add(faNotesMedical);
library.add(faUserShield);

// Components
Vue.component('font-awesome-icon', FontAwesomeIcon);
Vue.component('v-select', vSelect);

// Install Plugins
Vue.use(BootstrapVue);
Vue.use(Notifications);
Vue.use(Moment);
Vue.use(UUID);
Vue.use(Vuex);

// Google analytics, and don't ask why I didn't set it up this way the first time
Vue.use(VueAnalytics, {
  id: process.env.VUE_APP_GOOGLE_ANALYTICS_ID,
  router,
  debug: {
    enabled: false,
    trace: true,
  },
});


// Vue Global configs
if (process.env.VUE_APP_ENV === 'development' || process.env.VUE_APP_ENV === 'qa') {
  Vue.config.productionTip = true;
  Vue.config.devtools = true;
} else {
  Vue.config.productionTip = false;
  Vue.config.devtools = false;
}

// Setup axios
axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.headers.common.Accept = 'application/json';
// fetch token if it exists on first load.
const token = getToken();
if (token) {
  axios.defaults.headers.common.Authorization = `Bearer ${token}`;
}
// response interceptors to dictate login issues or server response errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      if (error.response.status === 401 && router.currentRoute.name !== 'Login') { // Unauthorized
        destroyToken();
        router.push({ name: 'Login' });
        Vue.notify({
          title: i18n.getLocaleMessage(i18n.locale).unauthenticated.title,
          text: i18n.getLocaleMessage(i18n.locale).unauthenticated.text,
          type: 'error',
          width: 350,
        });
      } else if (error.response.status === 429) { // Rate Limited
        Vue.notify({
          title: i18n.getLocaleMessage(i18n.locale).tooManyRequests.title,
          text: i18n.getLocaleMessage(i18n.locale).tooManyRequests.text,
          type: 'error',
          width: 350,
        });
      } else if (error.response.status === 500 && Object.prototype.hasOwnProperty.call(error.response, 'data')) { // Internal Server Error
        console.error(error.response.data.error, error.response.data);
      }
    }
    return Promise.reject(error);
  },
);

const config = {
  apiKey: process.env.VUE_APP_HONEYBADGER_API_KEY,
  environment: process.env.VUE_APP_ENV,
};
Vue.use(HoneybadgerVue, config);

export default new Vue({
  router,
  store,
  i18n,
  render: (h) => h(App),
}).$mount('#app');
